import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Select, MenuItem, FormControl, InputLabel,
  Grid, Pagination, CircularProgress, Button, Tooltip as MuiTooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  InputAdornment, Divider, Tabs, Tab, TextField as MuiTextField,
} from '@mui/material';
import {
  ShoppingCart, Edit as EditIcon, Visibility, Refresh, AddCircleOutline,
  TrendingUp, TrendingDown, FilterList, SmartToy, Person, ShowChart,
  History, Assessment, CheckCircle, Warning, Storage, BarChart as BarChartIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import dayjs from '../utils/dayjs';
import { trading, bot } from '../services/api';
import TradeLifecycleChartDialog from '../components/trading/TradeLifecycleChart';

const fmtMoney = (v) => parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pnlColorOf = (v) => (parseFloat(v) >= 0 ? '#15803d' : '#b91c1c');

function formatZerodhaContract(symbol, expiryDate, strike, type) {
  if (!symbol) return '';
  const cleanStrike = strike ? Math.round(parseFloat(strike)) : '';
  if (!expiryDate) {
    return `${symbol} ${cleanStrike} ${type || ''}`.trim();
  }
  const d = dayjs(expiryDate);
  const day = d.date();
  const month = d.format('MMM').toUpperCase();
  const suffix = (day === 1 || day === 21 || day === 31) ? 'st'
    : (day === 2 || day === 22) ? 'nd'
    : (day === 3 || day === 23) ? 'rd'
    : 'th';
  return `${symbol} ${day}${suffix} ${month} ${cleanStrike} ${type || ''}`.trim();
}

// Every "placed by an AI agent" source tag across the platform's history — kept in one
// place so a new source (e.g. a future strategy) only needs to be added here once.
// AUTO_SENIOR_TRADER is the Senior Trader agent's equity source; AUTO/AUTO_AGENT are its
// legacy/option-position tags; AUTO_SCREENER is the NSE Stock Screener auto-trader.
const AI_SOURCES = new Set(['AUTO_SCREENER', 'AUTO', 'AUTO_AGENT', 'AUTO_SENIOR_TRADER']);

// ─── Update SL Dialog ────────────────────────────────────────────────────────
function UpdateSLDialog({ order, onClose, onSaved }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [refPrice, setRefPrice] = useState(parseFloat(order?.current_price || order?.ltp || order?.price || 0));
  const buyPrice = parseFloat(order?.ltp || order?.price || order?.entry_price || 0);

  useEffect(() => {
    if (!order) return;
    setValue(String(parseFloat(order.stop_loss || 0)));
    if (order.asset_type === 'EQUITY') {
      trading.equityOrderDetail(order.id)
        .then(res => {
          const live = parseFloat(res.data?.data?.current_price);
          if (!isNaN(live) && live > 0) setRefPrice(live);
        })
        .catch(() => {});
    }
  }, [order]);

  const handleSave = async () => {
    const sl = parseFloat(value);
    if (isNaN(sl) || sl <= 0) { toast.error('Enter a valid SL price'); return; }
    if (sl >= refPrice) { toast.error(`SL must be below current live price ₹${refPrice.toLocaleString('en-IN')}`); return; }
    setSaving(true);
    try {
      if (order.asset_type === 'EQUITY') {
        const res = await trading.updateEquityStopLoss(order.id, sl);
        toast.success(res.data.message || 'SL updated');
      } else {
        toast.success('SL updated successfully');
      }
      onSaved();
      onClose();
    } catch { /* toast shown by interceptor */ } finally { setSaving(false); }
  };

  if (!order) return null;
  const sl = parseFloat(value) || 0;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Update Stop-Loss — {order.symbol}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity={sl > buyPrice ? 'success' : 'info'} sx={{ py: 0.5 }}>
            {sl > buyPrice ? `Locking in profit: SL ₹${sl} > Buy ₹${buyPrice}` : `Protecting capital: SL ₹${sl} < Buy ₹${buyPrice}`}
          </Alert>

          <TextField
            label="Stop-Loss Price (₹)"
            type="number"
            value={value}
            onChange={e => setValue(e.target.value)}
            error={sl >= refPrice}
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f8fafc', p: 1.5, borderRadius: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Buy Price</Typography>
              <Typography variant="body2" fontWeight={700}>₹{buyPrice.toLocaleString('en-IN')}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">Current Live</Typography>
              <Typography variant="body2" fontWeight={700} color="primary.main">₹{refPrice.toLocaleString('en-IN')}</Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || sl >= refPrice}>
          {saving ? <CircularProgress size={18} /> : 'Save SL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Unified Master Orders Center Component ──────────────────────────────────
export default function MasterOrdersPage() {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('equity-orders')) return 1;
    if (location.pathname.includes('positions')) return 2;
    if (location.pathname.includes('history')) return 3;
    return 0;
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    if (location.pathname.includes('equity-orders')) setActiveTab(1);
    else if (location.pathname.includes('positions')) setActiveTab(2);
    else if (location.pathname.includes('history')) setActiveTab(3);
  }, [location.pathname]);
  const [equityOrders, setEquityOrders] = useState([]);
  const [paperPositions, setPaperPositions] = useState([]);
  const [livePositions, setLivePositions] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [mode, setMode] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [source, setSource] = useState('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [syncing, setSyncing] = useState(false);
  const [livePrices, setLivePrices] = useState({});

  // Dialogs
  const [slOrder, setSlOrder] = useState(null);
  const [viewOrderId, setViewOrderId] = useState(null);
  const [lifecycleOrderId, setLifecycleOrderId] = useState(null);
  const [lifecycleTradeId, setLifecycleTradeId] = useState(null);

  const handleOpenLifecycleChart = (order) => {
    if (order.asset_type === 'EQUITY' || !order.asset_type) {
      const rawId = order.raw_id || (typeof order.id === 'string' ? parseInt(order.id.replace(/\D/g, ''), 10) : order.id);
      setLifecycleOrderId(rawId);
    } else {
      const rawId = order.raw_id || (typeof order.id === 'string' ? parseInt(order.id.replace(/\D/g, ''), 10) : order.id);
      setLifecycleTradeId(rawId);
    }
  };

  const liveIntervalRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Pagination for this page is entirely client-side (see paginatedList below) —
      // every list here (equity, paper, live, history) is fetched in full and then
      // filtered/paginated locally. Passing the UI `page` through to this request too
      // caused page changes to silently swap out the underlying dataset out from under
      // the local slice, which is why paging past page 1 used to show wrong/empty rows.
      const eqParams = { page: 1, limit: 200 };
      if (mode !== 'ALL') eqParams.mode = mode;
      if (status !== 'ALL') eqParams.status = status;
      if (source !== 'ALL') eqParams.source = source;

      const [eqRes, paperPosRes, livePosRes, histRes] = await Promise.all([
        trading.equityOrders(eqParams).catch(() => ({ data: { data: [], total: 0 } })),
        trading.paperPositions({ status: 'ALL' }).catch(() => ({ data: { data: [] } })),
        trading.livePositions({ status: 'ALL' }).catch(() => ({ data: { data: [] } })),
        trading.history({ mode: mode === 'ALL' ? '' : mode, page: 1, limit: 100 }).catch(() => ({ data: { data: [], total: 0 } })),
      ]);

      const eqList = (eqRes.data?.data || []).map(r => ({ ...r, raw_id: r.id, asset_type: 'EQUITY', source: r.source || 'AUTO_SCREENER' }));
      const paperPosList = (paperPosRes.data?.data || []).map(r => {
        const contractLabel = formatZerodhaContract(r.symbol, r.expiry_date, r.strike_price, r.instrument_type);
        return {
          id: `POS-P-${r.id}`,
          raw_id: r.id,
          symbol: r.symbol,
          expiry_date: r.expiry_date,
          name: contractLabel || `${r.symbol} ${r.instrument_type || ''}`,
          quantity: r.quantity,
          lot_size: r.lot_size || (r.symbol === 'BANKNIFTY' ? 15 : 50),
          ltp: r.entry_price || r.price,
          sell_price: r.exit_price || r.current_price,
          current_price: r.current_price || r.entry_price,
          unrealized_pnl: r.unrealized_pnl || 0,
          realized_pnl: r.realized_pnl || r.net_pnl || 0,
          stop_loss: r.stop_loss,
          target: r.target,
          mode: 'PAPER',
          source: 'AUTO_AGENT',
          status: r.status === 'OPEN' ? 'PLACED' : (r.status === 'CLOSED' ? 'SOLD' : r.status),
          exit_reason: r.exit_reason,
          product: r.instrument_type || 'OPTION',
          placed_at: r.opened_at || r.created_at,
          sold_at: r.closed_at,
          asset_type: 'OPTION',
        };
      });

      const livePosList = (livePosRes.data?.data || []).map(r => {
        const contractLabel = formatZerodhaContract(r.symbol, r.expiry_date, r.strike_price, r.instrument_type);
        return {
          id: `POS-L-${r.id}`,
          raw_id: r.id,
          symbol: r.symbol,
          expiry_date: r.expiry_date,
          name: contractLabel || `${r.symbol} ${r.instrument_type || ''}`,
          quantity: r.quantity,
          lot_size: r.lot_size || (r.symbol === 'BANKNIFTY' ? 15 : 50),
          ltp: r.entry_price || r.price,
          sell_price: r.exit_price || r.current_price,
          current_price: r.current_price || r.entry_price,
          unrealized_pnl: r.unrealized_pnl || 0,
          realized_pnl: r.realized_pnl || r.net_pnl || 0,
          stop_loss: r.stop_loss,
          target: r.target,
          mode: 'LIVE',
          source: 'AUTO_AGENT',
          status: r.status === 'OPEN' ? 'PLACED' : (r.status === 'CLOSED' ? 'SOLD' : r.status),
          exit_reason: r.exit_reason,
          product: r.instrument_type || 'OPTION',
          placed_at: r.opened_at || r.created_at,
          sold_at: r.closed_at,
          asset_type: 'OPTION',
        };
      });

      const histList = (histRes.data?.data || []).map(r => {
        const contractLabel = r.instrument_type !== 'EQUITY'
          ? formatZerodhaContract(r.symbol, r.expiry_date, r.strike_price, r.instrument_type)
          : `${r.symbol} (EQUITY)`;
        return {
          id: `HIST-${r.id}`,
          raw_id: r.id,
          symbol: r.symbol,
          expiry_date: r.expiry_date,
          name: contractLabel,
          quantity: r.quantity,
          ltp: r.entry_price,
          sell_price: r.exit_price,
          realized_pnl: r.net_pnl,
          mode: r.trade_mode || 'PAPER',
          source: r.trade_mode === 'LIVE' ? 'MANUAL' : 'AUTO_AGENT',
          status: 'SOLD',
          exit_reason: r.exit_reason || r.trade_result,
          product: r.instrument_type || 'OPTION',
          placed_at: r.entry_time,
          sold_at: r.exit_time,
          asset_type: r.instrument_type === 'EQUITY' ? 'EQUITY' : 'OPTION',
        };
      });

      setEquityOrders(eqList);
      setPaperPositions(paperPosList);
      setLivePositions(livePosList);
      setTradeHistory(histList);
      setTotalRecords((eqRes.data?.total || 0) + paperPosList.length + livePosList.length);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [mode, status, source]);

  const [actionLoadingId, setActionLoadingId] = useState(null);

  const handleSellOrder = async (order) => {
    const isEquity = order.asset_type === 'EQUITY';
    const confirmMsg = isEquity
      ? `Are you sure you want to sell/square-off ${order.symbol} (${order.quantity} shares)?`
      : `Are you sure you want to close/square-off ${order.symbol} position (${order.quantity} lot)?`;
    if (!window.confirm(confirmMsg)) return;

    setActionLoadingId(order.id);
    try {
      if (isEquity) {
        const res = await trading.sellStock({ id: order.raw_id || order.id });
        toast.success(res.data?.message || `Successfully sold ${order.symbol}`);
      } else {
        const posId = order.raw_id || (typeof order.id === 'string' ? parseInt(order.id.replace(/^POS-[PL]-/, ''), 10) : order.id);
        const res = await trading.closePosition({ id: posId, mode: order.mode || 'PAPER' });
        toast.success(res.data?.message || `Successfully closed ${order.symbol}`);
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Sell action failed');
    } finally {
      setActionLoadingId(null);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await trading.syncZerodhaPortfolio();
      toast.success(res.data.message || 'Synced portfolio from Zerodha');
      loadData();
    } catch { /* handled */ } finally { setSyncing(false); }
  };

  // Combine normalized active orders
  const allActiveOrders = [...equityOrders, ...paperPositions, ...livePositions];
  
  // Filter logic
  let filteredList = [];
  if (activeTab === 0) {
    filteredList = allActiveOrders;
  } else if (activeTab === 1) {
    filteredList = equityOrders;
  } else if (activeTab === 2) {
    filteredList = [...paperPositions, ...livePositions];
  } else if (activeTab === 3) {
    filteredList = tradeHistory;
  }

  if (search.trim()) {
    const s = search.trim().toUpperCase();
    filteredList = filteredList.filter(o => o.symbol.includes(s) || (o.name && o.name.toUpperCase().includes(s)));
  }

  if (mode !== 'ALL') {
    filteredList = filteredList.filter(o => o.mode === mode);
  }

  if (source !== 'ALL' && activeTab !== 2) {
    if (source === 'AI_ALL') {
      filteredList = filteredList.filter(o => AI_SOURCES.has(o.source));
    } else if (source === 'AUTO_SCREENER') {
      filteredList = filteredList.filter(o => o.source === 'AUTO_SCREENER');
    } else if (source === 'AUTO_AGENT' || source === 'AUTO') {
      filteredList = filteredList.filter(o => o.source === 'AUTO_AGENT' || o.source === 'AUTO' || o.source === 'AUTO_SENIOR_TRADER');
    } else if (source === 'MANUAL') {
      filteredList = filteredList.filter(o => o.source === 'MANUAL');
    }
  }

  if (status !== 'ALL') {
    filteredList = filteredList.filter(o =>
      status === 'OPEN' ? (o.status === 'PLACED' || o.status === 'NEEDS_REVIEW' || o.status === 'OPEN') : o.status === status
    );
  }

  // Sort running orders 1st (newest placed first), then sold orders descending by sold date & time
  const isRunningOrder = (s) => s === 'PLACED' || s === 'NEEDS_REVIEW' || s === 'OPEN';
  const isSoldOrder = (s) => s === 'SOLD' || s === 'CLOSED' || s === 'SQUARED_OFF';

  const getStatusRank = (s) => {
    if (isRunningOrder(s)) return 0;
    if (isSoldOrder(s)) return 1;
    return 2;
  };

  filteredList.sort((a, b) => {
    const rankA = getStatusRank(a.status);
    const rankB = getStatusRank(b.status);
    if (rankA !== rankB) return rankA - rankB;

    if (rankA === 0) {
      // Running orders: newest placed_at first
      const timeA = a.placed_at ? new Date(a.placed_at).getTime() : 0;
      const timeB = b.placed_at ? new Date(b.placed_at).getTime() : 0;
      if (timeB !== timeA) return timeB - timeA;
      return (b.raw_id || b.id || 0) - (a.raw_id || a.id || 0);
    }

    if (rankA === 1) {
      // Sold orders: descending as per sold date and time
      const soldA = (a.sold_at || a.closed_at || a.exit_time) ? new Date(a.sold_at || a.closed_at || a.exit_time).getTime() : 0;
      const soldB = (b.sold_at || b.closed_at || b.exit_time) ? new Date(b.sold_at || b.closed_at || b.exit_time).getTime() : 0;
      if (soldB !== soldA) return soldB - soldA;
      const timeA = a.placed_at ? new Date(a.placed_at).getTime() : 0;
      const timeB = b.placed_at ? new Date(b.placed_at).getTime() : 0;
      if (timeB !== timeA) return timeB - timeA;
      return (b.raw_id || b.id || 0) - (a.raw_id || a.id || 0);
    }

    const timeA = a.placed_at ? new Date(a.placed_at).getTime() : 0;
    const timeB = b.placed_at ? new Date(b.placed_at).getTime() : 0;
    return timeB - timeA;
  });

  // P&L Metrics calculation
  const totalPnl = filteredList.reduce((sum, o) => {
    const isOpen = o.status === 'PLACED' || o.status === 'OPEN' || o.status === 'NEEDS_REVIEW';
    const live = livePrices[o.id] || livePrices[o.raw_id];
    const val = isOpen
      ? (live?.unrealized_pnl ?? parseFloat(o.unrealized_pnl ?? 0))
      : parseFloat(o.realized_pnl ?? 0);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const aiList = filteredList.filter(o => AI_SOURCES.has(o.source));
  const manualList = filteredList.filter(o => o.source === 'MANUAL');

  const aiPnl = aiList.reduce((sum, o) => {
    const isOpen = o.status === 'PLACED' || o.status === 'OPEN' || o.status === 'NEEDS_REVIEW';
    const live = livePrices[o.id] || livePrices[o.raw_id];
    const val = isOpen
      ? (live?.unrealized_pnl ?? parseFloat(o.unrealized_pnl ?? 0))
      : parseFloat(o.realized_pnl ?? 0);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const manualPnl = manualList.reduce((sum, o) => {
    const isOpen = o.status === 'PLACED' || o.status === 'OPEN' || o.status === 'NEEDS_REVIEW';
    const live = livePrices[o.id] || livePrices[o.raw_id];
    const val = isOpen
      ? (live?.unrealized_pnl ?? parseFloat(o.unrealized_pnl ?? 0))
      : parseFloat(o.realized_pnl ?? 0);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const activeEquityCount = equityOrders.filter(o => o.status === 'PLACED').length;
  const activeOptionsCount = paperPositions.length + livePositions.length;

  // Safe Dynamic Pagination Slice
  const maxPage = Math.max(1, Math.ceil(filteredList.length / pageSize));
  const currentPage = page > maxPage ? 1 : page;
  const paginatedList = filteredList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShoppingCart color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#0f172a' }}>
              Master Orders &amp; Positions Terminal
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Unified real-time management center for Stock Equities &amp; NIFTY/BANKNIFTY Options
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
            onClick={() => {
              loadData();
              toast.success('Orders refreshed');
            }}
            disabled={loading}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            Refresh
          </Button>
          <Button
            size="small" variant="outlined"
            startIcon={syncing ? <CircularProgress size={14} /> : <Refresh />}
            onClick={handleSync} disabled={syncing}
            sx={{ borderRadius: 2 }}
          >
            Sync Zerodha
          </Button>
        </Box>
      </Box>

      {/* Top Executive KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: totalPnl >= 0 ? '#f0fdf4' : '#fef2f2' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                NET COMBINED P&amp;L
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: totalPnl >= 0 ? '#15803d' : '#b91c1c', mt: 0.5 }}>
                {totalPnl >= 0 ? '+' : ''}{fmtMoney(totalPnl)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Stock Equities + NIFTY/BANKNIFTY Options
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: aiPnl >= 0 ? '#f0fdf4' : '#fff5f5' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 800, textTransform: 'uppercase' }}>
                  🤖 AI AGENT P&amp;L
                </Typography>
                <Chip label={`${aiList.length} Orders`} size="small" sx={{ fontWeight: 800, bgcolor: '#dcfce7', color: '#15803d', height: 20, fontSize: 10 }} />
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: aiPnl >= 0 ? '#15803d' : '#b91c1c', mt: 0.5 }}>
                {aiPnl >= 0 ? '+' : ''}{fmtMoney(aiPnl)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Automated Bot Trades P&amp;L
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: manualPnl >= 0 ? '#f8fafc' : '#fff5f5' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 800, textTransform: 'uppercase' }}>
                  👤 MANUAL TRADES P&amp;L
                </Typography>
                <Chip label={`${manualList.length} Orders`} size="small" sx={{ fontWeight: 800, bgcolor: '#f1f5f9', color: '#475569', height: 20, fontSize: 10 }} />
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: manualPnl >= 0 ? '#15803d' : '#b91c1c', mt: 0.5 }}>
                {manualPnl >= 0 ? '+' : ''}{fmtMoney(manualPnl)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Manual &amp; Synced Portfolio P&amp;L
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                ACTIVE POSITIONS
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
                <Typography variant="h5" fontWeight={800} color="#0f172a">{activeEquityCount + activeOptionsCount}</Typography>
                <Typography variant="caption" color="text.secondary">Active Running</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700, display: 'block' }}>
                📦 {activeEquityCount} Stocks · 📈 {activeOptionsCount} Options
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Filter & Tabs Card */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, bgcolor: '#f8fafc' }}>
          <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); setPage(1); }}>
            <Tab icon={<Storage sx={{ fontSize: 18 }} />} iconPosition="start" label={`🌐 ALL ORDERS (${allActiveOrders.length})`} sx={{ fontWeight: 800 }} />
            <Tab icon={<ShoppingCart sx={{ fontSize: 18 }} />} iconPosition="start" label={`📦 STOCK EQUITIES (${equityOrders.length})`} sx={{ fontWeight: 800 }} />
            <Tab icon={<ShowChart sx={{ fontSize: 18 }} />} iconPosition="start" label={`📈 NIFTY & BANKNIFTY (${paperPositions.length + livePositions.length})`} sx={{ fontWeight: 800 }} />
            <Tab icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" label={`📜 TRADE HISTORY (${tradeHistory.length})`} sx={{ fontWeight: 800 }} />
          </Tabs>
        </Box>

        <CardContent sx={{ pb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <MuiTextField
                fullWidth size="small"
                placeholder="Search Symbol (e.g. HINDALCO, NIFTY)..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                InputProps={{ startAdornment: <InputAdornment position="start">🔍</InputAdornment> }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Trading Mode</InputLabel>
                <Select value={mode} label="Trading Mode" onChange={e => { setMode(e.target.value); setPage(1); }}>
                  <MenuItem value="ALL">All Modes (Paper &amp; Live)</MenuItem>
                  <MenuItem value="PAPER">Paper Trading Mode</MenuItem>
                  <MenuItem value="LIVE">Live Zerodha Mode</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Source</InputLabel>
                <Select value={source} label="Order Source" onChange={e => { setSource(e.target.value); setPage(1); }}>
                  <MenuItem value="ALL">All Sources (AI &amp; Manual)</MenuItem>
                  <MenuItem value="AI_ALL">🤖 All AI Agents (Screener + Senior Trader)</MenuItem>
                  <MenuItem value="AUTO_SCREENER">📊 AI Screener Bot</MenuItem>
                  <MenuItem value="AUTO_AGENT">🧠 Senior Trader AI Agent</MenuItem>
                  <MenuItem value="MANUAL">👤 Manual / Synced</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={e => { setStatus(e.target.value); setPage(1); }}>
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  <MenuItem value="OPEN">Active Running (Open)</MenuItem>
                  <MenuItem value="SOLD">Sold / Closed</MenuItem>
                  <MenuItem value="NEEDS_REVIEW">Needs Review</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Unified Master Table */}
      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 800, bgcolor: '#f8fafc', py: 1.2, color: '#475569' } }}>
                  <TableCell>DATE &amp; TIME</TableCell>
                  <TableCell>ASSET / SYMBOL</TableCell>
                  <TableCell align="center">CATEGORY</TableCell>
                  <TableCell align="center">PLACED BY</TableCell>
                  <TableCell align="right">QTY</TableCell>
                  <TableCell align="right">BUY PRICE</TableCell>
                  <TableCell align="right">LIVE PRICE</TableCell>
                  <TableCell align="right">STOP LOSS</TableCell>
                  <TableCell align="right">P&amp;L</TableCell>
                  <TableCell align="center">MODE</TableCell>
                  <TableCell align="center">STATUS</TableCell>
                  <TableCell align="center">ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 5, color: '#64748b' }}>
                      No orders found matching your active filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedList.map(o => {
                    const isOpen = o.status === 'PLACED' || o.status === 'OPEN';
                    const isSold = o.status === 'SOLD' || o.status === 'CLOSED';
                    const needsReview = o.status === 'NEEDS_REVIEW';

                    const buyPrice = parseFloat(o.ltp || o.price || 0);
                    const live = livePrices[o.id] || livePrices[o.raw_id];
                    const currentPrice = live?.current_price ?? (o.current_price ? parseFloat(o.current_price) : (buyPrice > 0 ? buyPrice : null));
                    const pnl = isOpen
                      ? (live?.unrealized_pnl ?? parseFloat(o.unrealized_pnl ?? 0))
                      : parseFloat(o.realized_pnl ?? 0);
                    const pnlColor = pnl >= 0 ? '#15803d' : '#b91c1c';
                    const qty = parseInt(o.quantity || 1, 10);
                    const lotSize = parseInt(o.lot_size || (o.asset_type === 'OPTION' ? (o.symbol === 'BANKNIFTY' ? 15 : 50) : 1), 10);
                    const totalUnits = o.asset_type === 'OPTION' ? qty * lotSize : qty;
                    const investment = buyPrice * totalUnits;
                    const pnlPct = investment > 0 ? (pnl / investment) * 100 : (o.pnl_pct ? parseFloat(o.pnl_pct) : 0);

                    return (
                      <TableRow key={o.id} hover sx={{ bgcolor: needsReview ? '#fffdf5' : 'inherit' }}>
                        <TableCell onClick={() => handleOpenLifecycleChart(o)} sx={{ cursor: 'pointer' }}>
                          {isSold && (o.sold_at || o.closed_at || o.exit_time) ? (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#b91c1c', fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
                                Sold: {dayjs(o.sold_at || o.closed_at || o.exit_time).format('DD/MM/YY HH:mm')}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem', display: 'block', lineHeight: 1.2, mt: 0.3 }}>
                                Bought: {dayjs(o.placed_at).format('DD/MM/YY HH:mm')}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#64748b', '&:hover': { color: '#2563eb' } }}>
                              {dayjs(o.placed_at).format('DD/MM/YY HH:mm')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell onClick={() => handleOpenLifecycleChart(o)} sx={{ cursor: 'pointer' }}>
                          <Typography fontWeight={800} variant="body2" sx={{ color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
                            {o.name || o.symbol}
                          </Typography>
                          {o.asset_type === 'EQUITY' && o.name !== o.symbol && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {o.symbol}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={o.asset_type === 'EQUITY' ? '📦 EQUITY' : '📈 OPTION'}
                            sx={{ fontWeight: 800, fontSize: '0.7rem', bgcolor: o.asset_type === 'EQUITY' ? '#eff6ff' : '#faf5ff', color: o.asset_type === 'EQUITY' ? '#2563eb' : '#9333ea' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={o.source === 'AUTO_SCREENER' ? '📊 AI Screener' : (AI_SOURCES.has(o.source) ? '🧠 Senior Trader' : '👤 Manual')}
                            sx={{
                              fontWeight: 800,
                              fontSize: '0.7rem',
                              bgcolor: o.source === 'AUTO_SCREENER' ? '#dcfce7' : (AI_SOURCES.has(o.source) ? '#e0e7ff' : '#f1f5f9'),
                              color: o.source === 'AUTO_SCREENER' ? '#15803d' : (AI_SOURCES.has(o.source) ? '#3730a3' : '#475569'),
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <b>{o.quantity}</b>
                          {o.asset_type === 'OPTION' && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ({parseInt(o.quantity, 10) * parseInt(o.lot_size || (o.symbol === 'BANKNIFTY' ? 15 : 50), 10)} units)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{buyPrice.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">
                          {(isOpen || needsReview) ? (
                            currentPrice != null
                              ? <Typography variant="body2" fontWeight={700}>{currentPrice.toLocaleString('en-IN')}</Typography>
                              : <Typography variant="caption" color="text.disabled">—</Typography>
                          ) : isSold
                            ? parseFloat(o.sell_price || 0).toLocaleString('en-IN')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {isOpen && o.stop_loss ? (
                            <Typography variant="body2" fontWeight={700} color="error.main">
                              {parseFloat(o.stop_loss).toLocaleString('en-IN')}
                            </Typography>
                          ) : isSold && o.exit_reason ? (
                            <Chip size="small" label={o.exit_reason} sx={{ fontSize: 10, fontWeight: 700 }} />
                          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={800} sx={{ color: pnlColor }}>
                            {pnl >= 0 ? '+' : ''}{pnl.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={o.mode || 'LIVE'}
                            sx={{ bgcolor: o.mode === 'PAPER' ? '#e0f2fe' : '#fce7f3',
                                  color: o.mode === 'PAPER' ? '#0369a1' : '#be185d',
                                  fontWeight: 800, fontSize: 11 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={needsReview ? 'NEEDS REVIEW' : o.status}
                            color={isOpen ? 'success' : needsReview ? 'warning' : isSold ? 'default' : 'default'} sx={{ fontWeight: 800 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                            <MuiTooltip title="View Purchase-to-Sell Trajectory & Trailing Graph">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={(e) => { e.stopPropagation(); handleOpenLifecycleChart(o); }}
                                sx={{ fontSize: 11, fontWeight: 700, py: 0.2, px: 0.8, minWidth: 0, textTransform: 'none' }}
                                startIcon={<BarChartIcon sx={{ fontSize: 14 }} />}
                              >
                                Graph
                              </Button>
                            </MuiTooltip>
                            {isOpen && (
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                disabled={actionLoadingId === o.id}
                                onClick={() => handleSellOrder(o)}
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  py: 0.3,
                                  px: 1.2,
                                  minWidth: 50,
                                  textTransform: 'none',
                                  boxShadow: 'none',
                                  '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
                                }}
                              >
                                {actionLoadingId === o.id ? <CircularProgress size={13} color="inherit" /> : 'Sell'}
                              </Button>
                            )}
                            {isOpen && o.asset_type === 'EQUITY' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={actionLoadingId === o.id}
                                onClick={() => setSlOrder(o)}
                                sx={{ fontSize: 11, fontWeight: 700, py: 0.2, px: 1, minWidth: 0, textTransform: 'none' }}
                              >
                                Edit SL
                              </Button>
                            )}
                            {isSold && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Closed
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Dynamic Pagination Controls */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            Showing {paginatedList.length} of {filteredList.length} orders
          </Typography>
          <Pagination
            count={maxPage}
            page={currentPage}
            onChange={(_, v) => setPage(v)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Per page:</Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={e => { setPageSize(e.target.value); setPage(1); }}
              sx={{ height: 32, fontSize: 13 }}
            >
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>
        </Box>
      </Card>

      {/* Edit SL Dialog */}
      {slOrder && (
        <UpdateSLDialog
          order={slOrder}
          onClose={() => setSlOrder(null)}
          onSaved={loadData}
        />
      )}

      {/* Purchase-to-Sell Trajectory & Trailing Column Graph Dialogs */}
      {lifecycleOrderId && (
        <TradeLifecycleChartDialog
          open={!!lifecycleOrderId}
          orderId={lifecycleOrderId}
          onClose={() => setLifecycleOrderId(null)}
        />
      )}
      {lifecycleTradeId && (
        <TradeLifecycleChartDialog
          open={!!lifecycleTradeId}
          tradeId={lifecycleTradeId}
          onClose={() => setLifecycleTradeId(null)}
        />
      )}
    </Box>
  );
}
